/**
 * Context Binding Engine → Financial-Assumptions bridge (CBE phase 2a).
 *
 * The THIRD source for the financial "Pull from BOM + Intent" path. After
 * the BOM rollup + project-intent sync (`buildFinancialPullFromSources`)
 * have produced the per-phase + campus financial values, this bridge layers
 * the extracted-context fills ON TOP — but only as a COMPLEMENT:
 *
 *   - it GAP-FILLS the ~28-32 net-new fields the CBE owns (ppaPrice /
 *     escalator / term, itcPct, ptcPerMwhUsd, fuelCost, …) that neither BOM
 *     nor Intent reach,
 *   - it NEVER overwrites a BOM-owned capex or an Intent-owned capacity /
 *     in-service date (those are passed in as `'owner'` field states so the
 *     engine's `mergeResolutions` complements, never clobbers),
 *   - a same-field DIVERGENCE on a co-owned field (e.g. context CAPEX vs the
 *     BOM CAPEX) surfaces as a `CbeFieldConflict` — it is NOT silently
 *     picked,
 *   - a `'manual'` field is protected (never written; a divergence surfaces).
 *
 * RANGE DISCIPLINE (CLAUDE.md §2): every numeric field on
 * `FinancialAssumptionsCore` / `FinancialPhase` is a SCALAR (`number|null`).
 * A range CBE value therefore NEVER fills a scalar field with a midpoint —
 * it is recorded as a `rangeHint` so the UI can surface "$3.0-3.6B (range —
 * enter a point value)" rather than fabricating a single number.
 *
 * Provenance: every context-filled field is stamped `'extracted-context'`,
 * with `cellReference` carrying the originating claim tag + a verbatim
 * source-span excerpt so the review modal can render a "from {context}" chip
 * and the user can verify the fill against its source.
 *
 * This module is PURE (no I/O). The store action (`pullFinancialFromSources`)
 * gathers `site.siteContext.claims[]`, runs the engine, builds the field-state
 * map from the post-BOM+Intent FA, and applies the result here.
 */
import { filledResolutions, resolveContextBindings } from '../services/contextIngestion/resolveContextBindings';
import { mergeResolutions } from '../services/contextIngestion/mergeResolutions';
import { spanExcerpt } from '../services/contextIngestion/spanExcerpt';
import type {
  CbeFact,
  CbeValue,
} from '../services/contextIngestion/cbeTypes';
import type {
  CbeFieldConflict,
  CbeFieldFill,
  FieldAuthority,
  FieldState,
} from '../services/contextIngestion/mergeResolutions';
import type { ExtractedClaim } from '../types/siteContext';
import type {
  FieldProvenance,
  FinancialAssumptions,
  FinancialAssumptionsCore,
  FinancialPhase,
} from '../types/financialAssumptions';

/** Campus-core financial fields the CBE may write (scalar/date only). */
type WritableCoreField = Exclude<
  keyof FinancialAssumptionsCore,
  // landCost is an OBJECT — handled via the dot-path 'landCost.totalUsd'.
  'landCost'
>;

/** Per-phase financial fields the CBE may write. */
type WritablePhaseField = Extract<
  keyof FinancialPhase,
  'inServiceDate' | 'constructionMonths'
>;

/** A field the bridge actually filled, for the review surfaces + audit. */
export interface ContextFilledField {
  /** 'financial' (campus core) or 'financialPhase'. */
  surface: 'financial' | 'financialPhase';
  /** Dot-path on the surface (e.g. 'ppaPricePerMwhUsd', 'landCost.totalUsd'). */
  path: string;
  /** The value written. */
  value: number | string;
  /** Originating claim tag + verbatim source-span excerpt. */
  sourceRef: string;
  /** Whether the extractor confidence was below the floor (flagged). */
  lowConfidence: boolean;
}

/** A range CBE value that COULD NOT fill a scalar field (no midpoint). */
export interface ContextRangeHint {
  surface: 'financial' | 'financialPhase';
  path: string;
  min: number | null;
  max: number | null;
  unit?: string;
  sourceRef: string;
}

export interface ContextFinancialFillResult {
  /** FA with context fills applied to campus core + per-phase fields. */
  financialAssumptions: FinancialAssumptions;
  /** Fields actually filled from context this run. */
  filledFields: ContextFilledField[];
  /** Range values that were left unfilled (scalar field — no midpoint). */
  rangeHints: ContextRangeHint[];
  /** Same-field divergences the merge refused to silently resolve. */
  conflicts: CbeFieldConflict[];
}

/** Build the engine's {tag,value,sourceText,confidence} facts from claims. */
export function claimsToFacts(
  claims: readonly ExtractedClaim[] | null | undefined,
): CbeFact[] {
  if (!claims || claims.length === 0) return [];
  return claims.filter(c => c.state !== 'mis-extracted').map(c => ({
    tag: c.category,
    value: c.claimedValue,
    sourceText: c.sourceSpan,
    confidence: c.extractorConfidence,
    field: c.field,
    sedField: c.sedField,
  }));
}

/** Pull a scalar/string value out of a non-range CbeValue, or `null`. */
function scalarFromValue(v: CbeValue): number | string | null {
  switch (v.kind) {
    case 'scalar':
      return v.value;
    case 'date':
      return v.iso;
    case 'enum':
    case 'complex':
      return v.value;
    case 'bool':
      return v.value ? 'true' : 'false';
    case 'multi':
      return v.total;
    case 'range':
      return null; // never collapse a range to a scalar (no midpoint)
  }
}

// spanExcerpt moved to `../services/contextIngestion/spanExcerpt` (CBE-A2)
// so the SED-send renderer shares the exact provenance-excerpt truncation.

const CONTEXT_PROV = (
  fill: CbeFieldFill,
  now: string,
): FieldProvenance => ({
  source: 'extracted-context',
  // Low-confidence context fills render in the medium/low review tier.
  confidence: fill.lowConfidence ? 'low' : 'medium',
  cellReference: `context:${fill.value.provenance.tag} — "${spanExcerpt(fill.value.provenance.sourceSpan)}"`,
  extractedAt: now,
});

/**
 * Build the `FieldState` map the engine's merge consumes, from the FA the
 * BOM+Intent pull produced. Campus core fields and per-phase fields both
 * participate. Authority is derived from the value + its provenance:
 *   - no value                         ⇒ 'blank'  (context may gap-fill)
 *   - provenance.source 'manual'       ⇒ 'manual' (protected; divergence surfaces)
 *   - provenance.source 'derived'      ⇒ 'derived' (context may refresh)
 *   - any other populated value        ⇒ 'owner'  (BOM/Intent/extracted owns it;
 *                                                   context gap-fills nothing,
 *                                                   divergence surfaces)
 *
 * @param fa                 the post-BOM+Intent FinancialAssumptions.
 * @param phaseProvenance    `${phaseId}:${field}` → FieldProvenance from the pull.
 */
export function buildFinancialFieldStates(
  fa: FinancialAssumptions,
  phaseProvenance: Record<string, FieldProvenance> = {},
): Map<string, FieldState> {
  const states = new Map<string, FieldState>();

  const authorityOf = (value: unknown, prov: FieldProvenance | undefined): FieldAuthority => {
    if (value == null) return 'blank';
    if (!prov) return 'owner'; // populated but no provenance ⇒ user/system authored — protect
    if (prov.source === 'manual') return 'manual';
    if (prov.source === 'derived') return 'derived';
    return 'owner';
  };

  // ── Campus-core financial fields ────────────────────────────────────────
  const core = fa as FinancialAssumptionsCore;
  const coreKeys = Object.keys(core) as Array<keyof FinancialAssumptionsCore>;
  for (const key of coreKeys) {
    if (key === 'landCost') continue;
    const value = core[key];
    if (value !== undefined && (typeof value === 'number' || typeof value === 'string')) {
      const prov = fa.fieldProvenance?.[key];
      states.set(`financial:${key}`, { authority: authorityOf(value, prov), value });
    }
  }
  // landCost.totalUsd is the only nested core path the CBE targets.
  const landTotal = fa.landCost?.totalUsd ?? null;
  if (landTotal != null) {
    states.set('financial:landCost.totalUsd', { authority: 'owner', value: landTotal });
  }

  // ── Per-phase financial fields (inServiceDate / constructionMonths) ─────
  // The engine emits ONE field key per surface:path, so the per-phase slots
  // collapse to a single field state. Aggregate CONSERVATIVELY (most-
  // protective authority wins: manual > owner > derived > blank) so a single
  // populated phase prevents a clobber and surfaces a divergence; a fully-
  // blank field stays 'blank' for the context gap-fill. The recorded `value`
  // is the first populated phase's, used only for divergence comparison.
  const authorityRank: Record<FieldAuthority, number> = { manual: 3, owner: 2, derived: 1, blank: 0 };
  for (const field of ['inServiceDate', 'constructionMonths'] as const) {
    let best: FieldState | null = null;
    for (const phase of fa.phases ?? []) {
      const value = phase[field];
      if (value == null) continue;
      const prov = phaseProvenance[`${phase.phaseId}:${field}`];
      const cand: FieldState = { authority: authorityOf(value, prov), value };
      if (!best || authorityRank[cand.authority] > authorityRank[best.authority]) {
        best = cand;
      }
    }
    if (best) states.set(`financialPhase:${field}`, best);
  }

  return states;
}

const WRITABLE_PHASE_FIELDS: readonly WritablePhaseField[] = ['inServiceDate', 'constructionMonths'];

/** Is this a campus-core scalar/string/date field the CBE may write? */
function isWritableCoreField(path: string): path is WritableCoreField {
  // Every core field except `landCost` (object) is a scalar slot.
  return path !== 'landCost' && path !== 'landCost.totalUsd';
}

/**
 * Apply the engine's merge result to the FA. Pure — returns a NEW FA with the
 * context fills layered on campus core + per-phase fields, plus the filled /
 * range-hint / conflict surfaces.
 *
 * Only `financial` + `financialPhase` surface fills are consumed here (the
 * `intent` + `sed` surface fills are wired by their own callers in a later
 * phase). Per-phase fills are applied to EVERY phase that has a blank slot —
 * a campus-wide context fact (e.g. a single COD in the brief) is not
 * fabricated per-phase; it only fills a phase whose field is already blank
 * and whose `FieldState` allowed the write at merge time.
 */
export function applyContextFinancialFills(
  fa: FinancialAssumptions,
  fills: readonly CbeFieldFill[],
  conflicts: readonly CbeFieldConflict[],
  now: string,
): ContextFinancialFillResult {
  const filledFields: ContextFilledField[] = [];
  const rangeHints: ContextRangeHint[] = [];

  let nextCore: FinancialAssumptionsCore = { ...(fa as FinancialAssumptionsCore) };
  let nextPhases: FinancialPhase[] = (fa.phases ?? []).map(p => ({ ...p }));
  const fieldProvenance: FinancialAssumptions['fieldProvenance'] = { ...(fa.fieldProvenance ?? {}) };
  // Per-phase provenance (`${phaseId}:${field}` keys — the R-58 convention).
  // Stamped for every per-phase context fill so a later pull can tell the
  // CBE-sourced cell from a user-authored one (and the single-phase
  // backfill guard sees the value as sourced).
  const phaseFieldProvenance: Record<string, FieldProvenance> = { ...(fa.phaseFieldProvenance ?? {}) };
  let stampedPhaseProvenance = false;

  for (const fill of fills) {
    const { surface, path, value } = fill;
    const sourceRef = `${value.provenance.tag}: ${spanExcerpt(value.provenance.sourceSpan, 80)}`;

    // RANGE: never collapse to a scalar midpoint. Record a hint instead.
    if (value.kind === 'range') {
      if (surface === 'financial' || surface === 'financialPhase') {
        rangeHints.push({ surface, path, min: value.min, max: value.max, unit: value.unit, sourceRef });
      }
      continue;
    }

    const scalar = scalarFromValue(value);
    if (scalar === null) continue;

    if (surface === 'financial') {
      if (path === 'landCost.totalUsd') {
        if (typeof scalar !== 'number') continue;
        nextCore = {
          ...nextCore,
          landCost: { ...(nextCore.landCost ?? {}), totalUsd: scalar },
        };
        fieldProvenance['landCost.totalUsd'] = CONTEXT_PROV(fill, now);
        filledFields.push({ surface, path, value: scalar, sourceRef, lowConfidence: fill.lowConfidence });
        continue;
      }
      if (!isWritableCoreField(path)) continue;
      const key = path as WritableCoreField;
      // Type guard: a string value only lands on a string-typed core field
      // (inServiceDate / technology / depreciationMethod). Numeric fields
      // reject a string fill (the coercion should already match, but guard).
      (nextCore as unknown as Record<string, unknown>)[key] = scalar;
      fieldProvenance[key] = CONTEXT_PROV(fill, now);
      filledFields.push({ surface, path, value: scalar, sourceRef, lowConfidence: fill.lowConfidence });
      continue;
    }

    if (surface === 'financialPhase') {
      const field = path as WritablePhaseField;
      if (!WRITABLE_PHASE_FIELDS.includes(field)) continue;
      // Fill only phases whose slot is currently blank (gap-fill — never
      // clobber an already-populated per-phase value). A campus-wide context
      // fact fills each blank phase slot; a populated phase is untouched.
      let filledAny = false;
      nextPhases = nextPhases.map(p => {
        if (p[field] != null) return p;
        if (field === 'inServiceDate' && typeof scalar === 'string') {
          filledAny = true;
          phaseFieldProvenance[`${p.phaseId}:${field}`] = CONTEXT_PROV(fill, now);
          stampedPhaseProvenance = true;
          return { ...p, inServiceDate: scalar };
        }
        if (field === 'constructionMonths' && typeof scalar === 'number') {
          filledAny = true;
          phaseFieldProvenance[`${p.phaseId}:${field}`] = CONTEXT_PROV(fill, now);
          stampedPhaseProvenance = true;
          return { ...p, constructionMonths: scalar };
        }
        return p;
      });
      if (filledAny) {
        filledFields.push({ surface, path, value: scalar, sourceRef, lowConfidence: fill.lowConfidence });
      }
      continue;
    }
    // 'intent' / 'sed' surfaces are handled by their own callers — ignore here.
  }

  return {
    financialAssumptions: {
      ...fa,
      ...nextCore,
      phases: nextPhases,
      fieldProvenance,
      // Only attach the per-phase map when it carries entries — an absent
      // map keeps its "no auto-pull has run yet" meaning for readers.
      ...(stampedPhaseProvenance || fa.phaseFieldProvenance
        ? { phaseFieldProvenance }
        : {}),
    },
    filledFields,
    rangeHints,
    conflicts: [...conflicts],
  };
}

export interface PullContextFinancialResult extends ContextFinancialFillResult {
  /** Total binding rows that matched + produced a value (filled resolutions). */
  resolvedCount: number;
}

/**
 * End-to-end CBE financial fill: claims → resolve → merge (against the
 * post-BOM+Intent field states) → apply to FA. Pure.
 *
 * @param fa               the post-BOM+Intent FinancialAssumptions.
 * @param claims           `site.siteContext.claims[]`.
 * @param phaseProvenance  the per-phase provenance from the BOM+Intent pull.
 * @param now              ISO timestamp for the provenance stamp.
 */
export function pullContextFinancialFromClaims(
  fa: FinancialAssumptions,
  claims: readonly ExtractedClaim[] | null | undefined,
  phaseProvenance: Record<string, FieldProvenance>,
  now: string,
): PullContextFinancialResult {
  const facts = claimsToFacts(claims);
  if (facts.length === 0) {
    return {
      financialAssumptions: fa,
      filledFields: [],
      rangeHints: [],
      conflicts: [],
      resolvedCount: 0,
    };
  }
  const resolutions = resolveContextBindings(facts);
  const filled = filledResolutions(resolutions);
  const fieldStates = buildFinancialFieldStates(fa, phaseProvenance);
  const merged = mergeResolutions(filled, fieldStates);
  // Consume only the financial + financialPhase surface fills here.
  const financialFills = merged.fills.filter(
    f => f.surface === 'financial' || f.surface === 'financialPhase',
  );
  const financialConflicts = merged.conflicts.filter(
    c => c.surface === 'financial' || c.surface === 'financialPhase',
  );
  const applied = applyContextFinancialFills(fa, financialFills, financialConflicts, now);
  return { ...applied, resolvedCount: filled.length };
}
